import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import {
  Checkbox,
  Grid,
  Heading,
  InputEmail,
  InputNumber,
  InputTelephone,
  InputText,
  RadioButton,
  RadioGroup,
  Text,
} from '@eneco/sparky';
import { wrap } from '@sitecore/common';
import type { ContactFormRendering } from '@sitecore/types/ContactForm';
import { UseFormRegister, Control, Controller, FieldErrors } from 'react-hook-form';

import type { FormFields } from '../util/types';

type Props = {
  register: UseFormRegister<FormFields>;
  fields: ContactFormRendering['fields'];
  control: Control<FormFields, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<FormFields>;
  canHaveBudgetManager: boolean;
  authorIsBudgetManager?: boolean;
};

const ContactInputs: FC<Props> = ({
  register,
  fields,
  control,
  errors,
  canHaveBudgetManager,
  authorIsBudgetManager,
}) => {
  const { contactDetailsTitle, budgetManagerContent } = fields.content;
  const {
    budgetManagerToggleFormField,
    salutationFormField,
    firstNameFormField,
    surnamePrepositionFormField,
    surnameFormField,
    phoneNumberFormField,
    emailAddressFormField,
    customerIdFormField,
  } = fields.formFields;
  return (
    <>
      <Heading size="S" as="h3">
        {wrap(contactDetailsTitle)}
      </Heading>
      {canHaveBudgetManager && (
        <Checkbox
          {...register('authorIsBudgetManager')}
          label={budgetManagerToggleFormField.value.label}
          hint={budgetManagerToggleFormField.value.hint}
        />
      )}
      {authorIsBudgetManager && (
        <Text size="BodyM" color="textLowEmphasis">
          {wrap(budgetManagerContent, <RichText html={budgetManagerContent.value} />)}
        </Text>
      )}
      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            label={salutationFormField.value.label}
            hint={salutationFormField.value.hint}
            value={value}
            onValueChange={onChange}
            error={errors.salutation?.message}>
            {salutationFormField?.value?.options?.map(option => (
              <RadioButton value={option.name} key={option.name}>
                {option.label}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />
      <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="6" alignY="end">
        <InputText
          {...register('firstName')}
          label={firstNameFormField.value.label}
          error={errors.firstName?.message}
          placeholder={firstNameFormField.value.placeholder}
          hint={firstNameFormField.value.hint}
        />
        <InputText
          {...register('surnamePreposition')}
          label={surnamePrepositionFormField.value.label}
          error={errors.surnamePreposition?.message}
          placeholder={surnamePrepositionFormField.value.placeholder}
          hint={surnamePrepositionFormField.value.hint}
          isOptional
        />
        <InputText {...register('surname')} label={surnameFormField.value.label} error={errors.surname?.message} />
      </Grid>
      <Grid columns={{ initial: '1', sm: '2' }} gap="6">
        <InputTelephone
          {...register('phoneNumber')}
          label={phoneNumberFormField.value.label}
          error={errors.phoneNumber?.message}
          autoComplete="tel"
          placeholder={phoneNumberFormField.value.placeholder}
          hint={phoneNumberFormField.value.hint}
        />

        <InputEmail
          {...register('emailAddress')}
          label={emailAddressFormField.value.label}
          error={errors.emailAddress?.message}
          autoComplete="email"
          placeholder={emailAddressFormField.value.placeholder}
          hint={emailAddressFormField.value.hint}
        />
      </Grid>
      <InputNumber
        isOptional
        {...register('customerId')}
        label={customerIdFormField.value.label}
        error={errors.customerId?.message}
        formatOptions={{ useGrouping: false }}
        placeholder={customerIdFormField.value.placeholder}
        hint={customerIdFormField.value.hint}
      />
    </>
  );
};

export default ContactInputs;
